import { useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SyntheticEvent, useEffect, useState } from 'react';

import { IUserFromStorage } from '../../Interfaces/IUser';
import { listarPreguntas } from '../../Services/PreguntasFrecuentes';
import { IDataPreguntas, IPreguntas, Contenidos } from '../../Interfaces/IPreguntasFrecuentes';
import Loading from '../../Utils/Loading';
import VideoPlayer from '../../Components/VideoPlayer/VideoPlayer';
import ModalSupport from '../../Components/ModalSupport/ModalSupport';
import ModalSeguimiento from '../../Components/ModalSeguimiento/ModalSeguimiento';

import PreguntasDisplay from '../../Shared/Images/display_fit4_preguntas_y_respuestas.jpg';

import '../../Shared/css/FAQ/preguntasFrecuentes.css';

/* ── Inline FAQ Accordion ── */
const FaqItem = ({ pregunta }: { pregunta: IPreguntas }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={`pf-faq-item ${open ? 'pf-faq-item--open' : ''}`}>
      <button className="pf-faq-question" onClick={() => setOpen(!open)}>
        <span className="pf-faq-dot" />
        <span className="pf-faq-title">{pregunta.titulo}</span>
        <svg className="pf-faq-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="pf-faq-answer">
          {pregunta.contenidos.map((item, i) => (
            <AnswerContent key={i} item={item} />
          ))}
        </div>
      )}
    </div>
  );
};

const AnswerContent = ({ item }: { item: Contenidos }) => {
  const [showVideo, setShowVideo] = useState(false);

  if (item.tipo === 'T') {
    return (
      <div className="pf-faq-answer-text" dangerouslySetInnerHTML={{ __html: item.contenido }} />
    );
  }

  if (item.tipo === 'V') {
    return showVideo ? (
      <VideoPlayer url={item.contenido} onEnded={() => setShowVideo(false)} autoplay />
    ) : (
      <div className="pf-answer-video">
        <img src={item.portada} alt="portada" />
        <div className="pf-answer-play-overlay" onClick={() => setShowVideo(true)}>
          <div className="pf-answer-play-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
              <polygon points="6,3 20,12 6,21" />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

/* ── Main Page ── */
function PreguntasFrecuentes() {
  const [activeTab, setActiveTab] = useState(0);
  const [mostrarVideo, setMostrarVideo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dataPreguntas, setDataPreguntas] = useState<IDataPreguntas[]>([]);
  const [isOpenModalSeguimiento, setIsOpenModalSeguimiento] = useState(false);
  const [isOpenModalSupport, setIsOpenModalSupport] = useState(false);

  const isMobile = useMediaQuery('(max-width: 650px)');

  const user: IUserFromStorage = JSON.parse(localStorage.getItem('user') || '{}');
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    listarPreguntas({ UsuarioTestAsesoriaID: user.usuarioTestAsesoriaID })
      .then((res) => setDataPreguntas(res.data.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="pf-page">
      {/* Header */}
      <div className="pf-header" style={!isMobile ? { marginTop: '5vh'} : {}}>
        <button className="pf-back-btn" onClick={() => navigate('/producto/2')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div>
          <span className="pf-header-label">Centro de ayuda</span>
          <h1 className="pf-header-title">Preguntas Frecuentes</h1>
        </div>
      </div>

      <div className="pf-content">
        {loading ? (
          <div className="pf-loading">
            <Loading />
          </div>
        ) : (
          <>
            {/* Video */}
            <div className="pf-video-wrapper">
              {!mostrarVideo ? (
                <>
                  <img src={PreguntasDisplay} alt="Preguntas frecuentes" />
                  <div className="pf-play-overlay" onClick={() => setMostrarVideo(true)}>
                    <div className="pf-play-btn">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                        <polygon points="6,3 20,12 6,21" />
                      </svg>
                    </div>
                  </div>
                </>
              ) : (
                <VideoPlayer
                  url="https://player.vimeo.com/external/996830985.m3u8?s=47b1342df1ef4d9d1fb554d4d27062631375d6ba&logging=false"
                  onEnded={() => setMostrarVideo(false)}
                  autoplay
                />
              )}
            </div>

            {/* Info Card */}
            <div className="pf-info-card">
              <div className="pf-info-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </div>
              <p className="pf-info-text">
                <strong>Antes de escribirnos:</strong> Encuentra aquí las respuestas a las preguntas más frecuentes. Es probable que ya hayamos respondido la que tienes en mente.
              </p>
            </div>

            {/* Seguimiento Button (conditional) */}
            {user && user.usuarioTestAsesoriaID === 18 && (
              <button className="pf-seguimiento-btn" onClick={() => setIsOpenModalSeguimiento(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
                </svg>
                ¿Existe seguimiento 1 a 1?
              </button>
            )}

            {/* Category Tabs */}
            {dataPreguntas && dataPreguntas.length > 0 && (
              <div className="pf-tabs">
                {dataPreguntas.map((item, index) => (
                  <button
                    key={index}
                    className={`pf-tab ${activeTab === index ? 'pf-tab--active' : ''}`}
                    onClick={() => setActiveTab(index)}
                  >
                    {item.nombre}
                  </button>
                ))}
              </div>
            )}

            {/* FAQ List */}
            {dataPreguntas && dataPreguntas[activeTab] && (
              <div className="pf-faq-list">
                {dataPreguntas[activeTab].preguntas.map((pregunta, i) => (
                  <FaqItem key={pregunta.fit4PreguntaFrecuenteID || i} pregunta={pregunta} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {isOpenModalSeguimiento && (
        <ModalSeguimiento
          isOpen={isOpenModalSeguimiento}
          setIsOpen={setIsOpenModalSeguimiento}
        />
      )}

      {isOpenModalSupport && (
        <ModalSupport
          isOpen={isOpenModalSupport}
          setIsOpen={setIsOpenModalSupport}
        />
      )}
    </div>
  );
}

export default PreguntasFrecuentes;
